import { useField, useStore } from '@tanstack/react-form'
import { DataPolicyOperator, DataPolicyRowEffect, RowPolicyInput } from '@vendia/management-api-types'
import clsx from 'clsx'
import debug from 'debug'
import { getValue } from 'src/utils/form/form'
import { dataProductOperatorOptions, getColumnOptions } from 'src/utils/lakehouse/flows'
import { LakehouseFlowStepValues } from 'src/utils/lakehouse/types'

import Button from '../buttons/button'
import ListboxField from '../fields/listbox.field'
import TextField from '../fields/text.field'
import { StepComponentProps, VendiaFlowFormApi } from '../flows/types'
import Icon from '../icons/icon'

const logger = debug('app:sharingPermissions')

export const EditRowPolicies = ({
  context,
  policiesForm,
  fieldPath,
}: StepComponentProps<LakehouseFlowStepValues> & { policiesForm: VendiaFlowFormApi<any>; fieldPath?: string }) => {
  const selectedTableColumns = useStore(context.form.store, (state) => state.values.selectedTableColumns)
  const { rowPolicies } = useStore(policiesForm.store, (state) =>
    fieldPath ? getValue(state.values, fieldPath) : state.values,
  )

  const field = useField({
    name: fieldPath ? `${fieldPath}.rowPolicies` : 'rowPolicies',
    form: policiesForm,
    mode: 'array',
    defaultValue: rowPolicies,
  })

  const snowflakeColumnOptions = getColumnOptions(selectedTableColumns, rowPolicies)

  async function addRowPolicy(field: any) {
    await field.validate('change')
    if (field.state.meta.errors?.length === 0) {
      field.pushValue({ name: '', effect: DataPolicyRowEffect.Allow, operator: '', value: '' })
    }
  }

  const clearRowPolicy = (index: number) => {
    const policyAttributes = policiesForm.getFieldValue(`product.rowPolicies.${index}`)
    // This is required because removeValue() does not remove the nested fields
    Object.keys(policyAttributes).forEach((attribute) =>
      policiesForm.deleteField(`product.rowPolicies.${index}.${attribute}`),
    )
    field.removeValue(index)
  }

  return (
    <div className='flex w-full flex-1 flex-col'>
      {rowPolicies?.length === 0 && (
        <div className='text-light mb-6 mt-2 text-sm'>
          <div>All rows are included by default. Exclude rows by configuring rules.</div>
        </div>
      )}
      {rowPolicies?.length > 0 && (
        <div className={clsx('mb-4 flex h-5/6 w-full max-w-7xl flex-col justify-center gap-4')}>
          {rowPolicies?.map((policy: RowPolicyInput, index: number) => {
            const fieldPrefix = fieldPath ? `${fieldPath}.rowPolicies.${index}` : `rowPolicies.${index}`
            logger('row policy', { fieldPrefix, index, policy })

            const requireConstraint =
              policy.operator && ![DataPolicyOperator.IsNotNull, DataPolicyOperator.IsNull].includes(policy.operator)

            return (
              <div
                key={index}
                className={'rounded-md border border-slate-300 bg-slate-50 px-4 py-2'}
                data-testid={`row-sharing-policy-${index}`}
              >
                <div className='grid grid-cols-[auto,2fr,auto,1fr,1fr,auto] items-center gap-4'>
                  <span className='text-neutral-8 min-w-36 self-center'>
                    <strong> {index === 0 ? 'Filter data,' : 'and'}</strong>
                    {' where'}
                  </span>
                  <ListboxField
                    name={`${fieldPrefix}.name`}
                    label='Attribute'
                    options={snowflakeColumnOptions}
                    data-testid={`row-policy-attribute-select-${index}`}
                    validators={{
                      onChange: ({ value }) => (!value ? 'Attribute is required' : undefined),
                    }}
                    useNestedLabel
                    form={policiesForm}
                    wrapperClassName='!mb-0'
                  />
                  <span className='text-neutral-8 self-center'>is</span>
                  <ListboxField
                    name={`${fieldPrefix}.operator`}
                    label='Operator'
                    options={dataProductOperatorOptions}
                    data-testid={`row-policy-operator-select-${index}`}
                    validators={{
                      onChange: ({ value }) => (!value ? 'Operator is required' : undefined),
                    }}
                    useNestedLabel
                    form={policiesForm}
                    wrapperClassName='!mb-0'
                  />
                  {requireConstraint ? (
                    <TextField
                      name={`${fieldPrefix}.value`}
                      label='Constraint'
                      data-testid={`row-policy-value-input-${index}`}
                      wrapperClassName='!mb-0'
                      validators={{
                        onChange: ({ value }) => (!value ? 'Constraint is required' : undefined),
                      }}
                      useNestedLabel
                      form={policiesForm}
                    />
                  ) : (
                    <div></div>
                  )}
                  <div className={'py-2'}>
                    <Button kind='icon' icon='trash' onClick={() => clearRowPolicy(index)} />
                  </div>
                </div>
                {field.state.meta.errors?.length > 0 && (
                  <div className='flex gap-1 py-1 text-left text-xs'>
                    <Icon name='error' size='xs' />
                    {field.state.meta.errors.at(0)}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      <div className='flex w-full max-w-4xl pt-2'>
        <Button
          kind={'secondary'}
          onClick={(e) => {
            e.preventDefault()
            addRowPolicy(field)
          }}
          icon='plus-m'
          iconSize={14}
        >
          {field.state.value?.length > 0 ? 'Add another' : 'Add rule'}
        </Button>
      </div>
    </div>
  )
}
